import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import actions from "actions";
import CallNotStartedDialog from "./CallNotStartedDialog";
import CancelSessionDialog from "../WeeklyCalendarSessions/CancelSessionDialog";
import { getCancellationReasonLabel, getKeyCancellationReason } from "@constants/cancelReasons";
import { getClinicianCallsNeverStarted } from "../../selectors";
import useGetCancellationReasons from "hooks/api/appointments/useGetCancellationReasons";

/**
 * CallsNotStartedHandler
 * @param {Object} props
 * @param {string} props.clinicianId - The clinician's id
 */
export default function CallsNotStartedHandler({ clinicianId }) {
  const dispatch = useDispatch();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [details, setDetails] = useState(null);
  const [reason, setReason] = useState("");
  const [responsibleForCancellation, setResponsibleForCancellation] = useState("");
  const [cancelReasonText, setCancelReasonText] = useState("");
  const [cancellationReasonId, setCancellationReasonId] = useState("");
  const [cancelSessionOpen, setCancelSessionOpen] = useState(false);
  const [cancelAllInstances, setCancelAllInstances] = useState(false);
  const [manageCall, setManageCall] = useState(null);
  const {
    cancellationReasons,
    isLoading: cancellationReasonsLoading,
  } = useGetCancellationReasons();
  const {
    cancellationReasons: recurringCancellationReasons,
    isLoading: recurringCancellationReasonsLoading,
  } = useGetCancellationReasons(true);

  const callsNeverStarted = useSelector(getClinicianCallsNeverStarted);

  useEffect(() => {
    if (clinicianId) {
      dispatch(actions.getClinicianCallsNeverStarted(clinicianId));
    }
  }, [clinicianId, dispatch]);

  // Track previous calls length to only open dialog when length changes and is > 0
  const prevCallsLength = useRef(0);
  useEffect(() => {
    const currentLength = callsNeverStarted?.length ?? 0;
    if (prevCallsLength.current !== currentLength && currentLength > 0) {
      setDetails(callsNeverStarted[0]);
      setReason("");
      setCancelReasonText("");
      setCancellationReasonId("");
      setResponsibleForCancellation("");
      setDialogOpen(true);
    }
    prevCallsLength.current = currentLength;
  }, [callsNeverStarted]);

  // Refetch calls when both dialogs are closed
  const prevDialogOpen = useRef(false);
  const prevCancelOpen = useRef(false);
  useEffect(() => {
    if ((prevDialogOpen.current || prevCancelOpen.current) && !dialogOpen && !cancelSessionOpen) {
      dispatch(actions.getClinicianCallsNeverStarted(clinicianId));
    }
    prevDialogOpen.current = dialogOpen;
    prevCancelOpen.current = cancelSessionOpen;
  }, [dialogOpen, cancelSessionOpen, dispatch, clinicianId]);

  const handleClose = () => {
    setDialogOpen(false);
    setDetails(null);
    setReason("");
    setCancellationReasonId("");
    setCancelAllInstances(false);
  };

  const handleSubmit = () => {
    if (!details || !reason) return;
    if (reason === "cancel") {
      setManageCall(details);
      setCancelSessionOpen(true);
      setDialogOpen(false);
    } else if (reason === "no-show") {
      dispatch(actions.markClientNoShow(details.id));
      dispatch(actions.getClinicianCallsNeverStarted(clinicianId));
      handleClose();
    }
  };

  const onChangeResponsibleForCancellation = (e) => {
    const activeCancellationReasons = cancelAllInstances
      ? recurringCancellationReasons
      : cancellationReasons;
    if (Array.isArray(activeCancellationReasons) && activeCancellationReasons.length > 0) {
      setCancellationReasonId("");
      setResponsibleForCancellation(e.target.value);
      setCancelReasonText("");
      return;
    }

    setResponsibleForCancellation(e.target.value);
    setCancelReasonText(getCancellationReasonLabel(e.target.value));
  };

  const onChangeCancelReason = (e) => {
    const activeCancellationReasons = cancelAllInstances
      ? recurringCancellationReasons
      : cancellationReasons;
    const cancellationReason = (activeCancellationReasons || []).find(
      (reason) => String(reason.id) === String(e.target.value)
    );
    if (cancellationReason) {
      setCancellationReasonId(cancellationReason.id);
      setCancelReasonText(cancellationReason.displayLabel);
      setResponsibleForCancellation(cancellationReason.responsibleForCancellation);
      return;
    }

    setCancellationReasonId("");
    setCancelReasonText(e.target.value);
  };

  const onChangeCancelSelection = (e) => {
    setCancelAllInstances(e.target.value === "true");
    setCancellationReasonId("");
    setCancelReasonText("");
    setResponsibleForCancellation("");
  };

  const submitCancelSession = () => {
    const activeCancellationReasons = cancelAllInstances
      ? recurringCancellationReasons
      : cancellationReasons;
    const selectedCatalogReason = (activeCancellationReasons || []).find(
      (reason) => String(reason.id) === String(cancellationReasonId)
    );
    const selectedCatalogReasonId = selectedCatalogReason?.id;
    dispatch(
      actions.cancelVideoCall({
        message: cancelReasonText,
        responsibleForCancellation:
          selectedCatalogReasonId !== undefined
            ? responsibleForCancellation
            : getKeyCancellationReason(responsibleForCancellation),
        ...(selectedCatalogReasonId !== undefined
          ? { cancellationReasonId: selectedCatalogReasonId }
          : {}),
        id: manageCall.id,
        clinicianUserId: manageCall.clinician_user_id,
        clientId: manageCall.client_id,
        cancelAllInstances,
      })
    );
    dispatch(actions.getClinicianCallsNeverStarted(clinicianId));
    setCancelSessionOpen(false);
  };

  if (!details) return null;
  return (
    <>
      <CallNotStartedDialog
        sessionDetails={details}
        open={dialogOpen}
        handleClose={handleClose}
        callNotStartedReason={reason}
        onChangeCallNotStartedReason={(e) => setReason(e.target.value)}
        onSubmitCallNotStartedReason={handleSubmit}
      />
      {cancelSessionOpen && manageCall && (
        <CancelSessionDialog
          sessionDetails={manageCall}
          open={cancelSessionOpen}
          closeDialog={() => {
            setDialogOpen(true);
            setCancelSessionOpen(false);
            setCancellationReasonId("");
            setCancelReasonText("");
            setResponsibleForCancellation("");
            setCancelAllInstances(false);
          }}
          onChangeCancelReason={onChangeCancelReason}
          submitCancelSession={submitCancelSession}
          cancelReasonText={cancelReasonText}
          cancellationReasonId={cancellationReasonId}
          responsibleForCancellation={responsibleForCancellation}
          onChangeResponsibleForCancellation={onChangeResponsibleForCancellation}
          cancelAllInstances={cancelAllInstances}
          onChangeCancelSelection={onChangeCancelSelection}
          cancellationReasons={cancellationReasons}
          recurringCancellationReasons={recurringCancellationReasons}
          cancellationReasonsLoading={cancellationReasonsLoading}
          recurringCancellationReasonsLoading={recurringCancellationReasonsLoading}
          disableClose={true}
          hideAlert={true}
        />
      )}
    </>
  );
}
